/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Box,
    Text,
    Button,
    Heading
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl, FormattedMessage} from 'react-intl'
import {ChevronRightIcon, ChevronDownCheckout} from '../../../components/icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Components */
import DeliveryOptions from './delivery-options'
import Link from '@salesforce/retail-react-app/app/components/link'

/* Hooks */
import {useDeliveryOptions} from '../../../integrations/delivery-options/hooks/use-delivery-options'
import {useAccessToken} from '@salesforce/commerce-sdk-react'

/* Utils */
import {generateStyles} from '../../../components/optimizely/utils/utils'

const InformationAccordion = ({
    product,
    openFindInStoreModal,
    optimizelyContent,
    isOptimizelyLoading,
    isGiftPage = false,
    checkBalanceOnOpen
}) => {
    const {app: appConfig} = getConfig()
    const {formatMessage} = useIntl()
    const {getTokenWhenReady} = useAccessToken()
    const [token, setToken] = useState(null)
    const {deliveryOptionResponse} = useDeliveryOptions({accessToken: token, cache: true})
    const [deliveryOptionsData, setDeliveryOptionsData] = useState({})
    const [cmsDeliverySectionBlock, setCmsDeliverySectionBlock] = useState(null)
    const [isExpanded, setIsExpanded] = useState(false)

    const fetchToken = async () => {
        try {
            const token = await getTokenWhenReady()
            setToken(token)
        } catch (error) {
            console.error('Error fetching token:', error)
        }
    }

    useEffect(() => {
        fetchToken()
    }, [])

    useEffect(() => {
        if (deliveryOptionResponse?.success) {
            const filteredShippingMethods = Object.fromEntries(
                Object.entries(deliveryOptionResponse?.deliveryOptions || {}).filter(
                    ([, value]) => value !== null
                )
            )
            setDeliveryOptionsData(filteredShippingMethods)
        }
    }, [deliveryOptionResponse])

    useEffect(() => {
        if (!isOptimizelyLoading && optimizelyContent) {
            optimizelyContent.items[0].MainRegion.map((item, index) => {
                const contentBlock = item?.ContentLink?.Expanded
                if (contentBlock?.__typename === 'DeliverySectionBlock') {
                    setCmsDeliverySectionBlock(contentBlock)
                }
            })
        }
    }, [isOptimizelyLoading, optimizelyContent])

    // Function to split product details content
    const splitContent = (htmlContent) => {
        if (!htmlContent) return {beforeUl: '', afterUl: ''}

        const ulIndex = htmlContent.indexOf('<ul')

        if (ulIndex === -1) {
            // No <ul> found, return all content as visible
            return {beforeUl: htmlContent, afterUl: ''}
        }

        // Get content before the first <ul>
        const beforeUlContent = htmlContent.substring(0, ulIndex)

        // Find all non-empty <p> tags (ignore <p><br></p> and similar)
        const pTagMatches = beforeUlContent.match(/<p[^>]*>(?![\s<]*(?:<br|<\/p>)).*?<\/p>/gs)

        if (pTagMatches && pTagMatches.length > 0) {
            const lastPTag = pTagMatches[pTagMatches.length - 1]

            // Check if the last <p> tag contains 'Features'
            if (lastPTag.includes('Features')) {
                // Find the index of the last <p> tag
                const splitIndex = beforeUlContent.lastIndexOf(lastPTag)

                return {
                    beforeUl: htmlContent.substring(0, splitIndex),
                    afterUl: htmlContent.substring(splitIndex)
                }
            }
        }

        return {
            beforeUl: htmlContent.substring(0, ulIndex),
            afterUl: htmlContent.substring(ulIndex)
        }
    }

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded)
    }

    return (
        <Box>
            <Accordion allowMultiple w={'full'} defaultIndex={[0]}>
                {/* Details */}
                <AccordionItem border="none">
                    <AccordionButton data-testid="product-details-accordion-button" height="6" mb={2}>
                        <Heading as="h2" variant="accordionPdp" flex="1" textAlign="left">
                            {formatMessage({
                                defaultMessage: 'Product Details',
                                id: 'product_detail.accordion.button.product_detail'
                            })}
                        </Heading>
                        <AccordionIcon as={ChevronDownCheckout} />
                    </AccordionButton>
                    <AccordionPanel my={3} px={0} fontSize="xs">
                        <Box>
                            {!isGiftPage &&
                                (() => {
                                    const {beforeUl, afterUl} = splitContent(
                                        product?.longDescription
                                    )
                                    const hasHiddenContent = afterUl.length > 0

                                    return (
                                        <>
                                            <Box
                                                sx={{
                                                    p: {marginBottom: 1},
                                                    ul: {
                                                        marginTop: 1,
                                                        marginBottom: 4,
                                                        paddingLeft: 4,
                                                        listStyleType: 'disc'
                                                    }
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: beforeUl
                                                }}
                                            />
                                            {hasHiddenContent && (
                                                <>
                                                    <div
                                                        style={{
                                                            display: isExpanded ? 'block' : 'none'
                                                        }}
                                                    >
                                                            <Box
                                                                sx={{
                                                                    p: {fontWeight: 'medium'},
                                                                    ul: {
                                                                        marginTop: 1,
                                                                        marginBottom: 4,
                                                                        paddingLeft: 4,
                                                                        marginLeft: 2,
                                                                        listStyleType: 'disc'
                                                                    }
                                                                }}
                                                            >
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: afterUl
                                                                }}
                                                            />
                                                        </Box>
                                                        <Text mt={4}>
                                                            <FormattedMessage
                                                                defaultMessage="Style # {pid}"
                                                                id="product_detail.accordion.style"
                                                                values={{pid: product?.id}}
                                                            />
                                                        </Text>
                                                    </div>
                                                    <Button
                                                        variant="link"
                                                        onClick={toggleExpanded}
                                                        mt={2}
                                                        color="black"
                                                        textDecoration="underline"
                                                        fontSize="xs"
                                                        fontWeight="normal"
                                                        p={0}
                                                        height="auto"
                                                    >
                                                        {isExpanded
                                                            ? formatMessage({
                                                                  defaultMessage: 'See Less',
                                                                  id: 'product_detail.accordion.see_less'
                                                              })
                                                            : formatMessage({
                                                                  defaultMessage: 'See More',
                                                                  id: 'product_detail.accordion.see_more'
                                                              })}
                                                    </Button>
                                                </>
                                            )}
                                        </>
                                    )
                                })()}
                            {isGiftPage && (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: product?.shortDescription
                                    }}
                                />
                            )}
                        </Box>
                    </AccordionPanel>
                </AccordionItem>

                {/* Find In Store */}
                {appConfig?.FIND_IN_STORE_ENABLED === 'true' && !isGiftPage &&
                    <Button
                        variant="link"
                        textDecoration="none"
                        color="black"
                        w="full"
                        mb={2}
                        onClick={openFindInStoreModal} // Use the function here
                    >
                        <Heading
                            as="h3"
                            variant="accordionPdp"
                            textAlign="left"
                            justifyContent="space-between"
                            alignItems="center"
                            w="full"
                            display="flex"
                        >
                            {formatMessage({
                                defaultMessage: 'Find In Store',
                                id: 'product_detail.accordion.button.find_in_store'
                            })}
                            <ChevronRightIcon />
                        </Heading>
                    </Button>
                }

                {/* Check balance */}
                {appConfig?.SX_ENABLED == 'true' && isGiftPage &&
                    <Button
                        variant="link"
                        textDecoration="none"
                        color="black"
                        w="full"
                        mb={2}
                        onClick={checkBalanceOnOpen} // Use the function here
                    >
                        <Heading
                            as="h3"
                            variant="accordionPdp"
                            textAlign="left"
                            justifyContent="space-between"
                            alignItems="center"
                            w="full"
                            display="flex"
                        >
                            {formatMessage({
                                defaultMessage: 'Check Balance',
                                id: 'product_detail.accordion.button.check_balance'
                            })}
                            <ChevronRightIcon />
                        </Heading>
                    </Button>
                }

                {/* Gift Card Sections - FAQ and Terms */}
                {isGiftPage && (
                    <>
                        {/* FAQ Section */}
                        <Link
                            href="/faq.html"
                            textDecoration="none"
                            color="black"
                            w="full"
                            mb={2}
                            display="block"
                        >
                            <Heading
                                as="h3"
                                variant="accordionPdp"
                                textAlign="left"
                                justifyContent="space-between"
                                alignItems="center"
                                w="full"
                                display="flex"
                            >
                                {formatMessage({
                                    defaultMessage: 'FAQ',
                                    id: 'product_detail.accordion.button.faq'
                                })}
                                <ChevronRightIcon />
                            </Heading>
                        </Link>

                        {/* Terms and Conditions Section */}
                        <Link
                            href="/terms-conditions.html"
                            textDecoration="none"
                            color="black"
                            w="full"
                            display="block"
                        >
                            <Heading
                                as="h3"
                                variant="accordionPdp"
                                textAlign="left"
                                justifyContent="space-between"
                                alignItems="center"
                                w="full"
                                display="flex"
                            >
                                {formatMessage({
                                    defaultMessage: 'Terms and Conditions',
                                    id: 'product_detail.accordion.button.terms_and_conditions'
                                })}
                                <ChevronRightIcon />
                            </Heading>
                        </Link>
                    </>
                )}

                {/* Shipping and Pickup Options */}
                {!isGiftPage && (
                    <AccordionItem border="none">
                        {deliveryOptionsData && Object.keys(deliveryOptionsData)?.length > 0 && (
                            <AccordionItem border="none">
                                <AccordionButton data-testid="shipping-pickup-accordion-button" height="6">
                                    <Heading
                                        as="h4"
                                        variant="accordionPdp"
                                        flex="1"
                                        textAlign="left"
                                        {...(generateStyles(cmsDeliverySectionBlock?.DeliverySectionBlockTitleStyle))}
                                    >
                                        {cmsDeliverySectionBlock?.Title ? (
                                            cmsDeliverySectionBlock?.Title
                                        ) : (
                                            formatMessage({
                                                defaultMessage: 'Shipping & Pickup Options',
                                                id: 'product_detail.accordion.button.shipping_and_pickup_options'
                                            })
                                        )}
                                    </Heading>
                                    <AccordionIcon as={ChevronDownCheckout} />
                                </AccordionButton>
                                <AccordionPanel mb={6} mt={{base: 3}} p={{base: 0}} fontSize={'xs'}>
                                    <DeliveryOptions
                                        key="pdd-shipping-methods"
                                        shippingMethodsData={deliveryOptionsData}
                                        cmsDeliverySectionBlock={cmsDeliverySectionBlock}
                                    />
                                </AccordionPanel>
                            </AccordionItem>
                        )}
                    </AccordionItem>
                )}
            </Accordion>
            <Box display={['none', 'none', 'none', 'block']} flex={4}></Box>
        </Box>
    )
}

InformationAccordion.propTypes = {
    product: PropTypes.object,
    openFindInStoreModal: PropTypes.func,
    optimizelyContent: PropTypes.object,
    isOptimizelyLoading: PropTypes.bool,
    isGiftPage: PropTypes.bool
}

export default InformationAccordion
